import React, { useState, useEffect, useMemo } from 'react';

import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TLHClassesAPICalls from 'services/TLHClassesAPICalls';
import { Typography, Modal, Box, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import Button from '@mui/material/Button';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import TLHEndpoints from 'services/TLHEndpoints';
// import TLHClassesAPICalls from 'services/TLHClassesAPICalls.js';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import OneToOneIndividualLessonCard from '../layouts/HelperComponents/OneToOneIndividualLessonCard.js';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import * as CONSTANTS from '../../StringConstants.js';
import { useHistory } from 'react-router';

function mapAttendanceStatus(backendStatus) {
  switch (backendStatus) {
    case "PRESENT":
    case "LATE":
    case "LEFT_EARLY":
      return "attended";
    case "ABSENT":
    case "SICK":
    case "NOT_JOINED":
    case "LEFT":
    case "MISSED":
      return "missed";
    case "PENDING":
    case "HOLIDAY":
    case "OTHER":
    default:
      return "neutral";
  }
}

function mapCardStatusToBackend(cardStatus) {
  switch (cardStatus) {
    case "attended": return "PRESENT";
    case "missed":   return "MISSED";
    default:         return "PENDING";
  }
}

// Modal Style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

  // Define the styled TableCell for sticky column
  const StickyTableCell = styled(TableCell)(({ theme }) => ({
    position: 'sticky',
    left: 0,
    // backgroundColor: theme.palette.background.paper, // Adjust the color as needed
    zIndex: 1,
    borderRight: '1px solid #ddd', // Optional: to show a visual separation
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.paper,
      color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
      backgroundColor: theme.palette.background.paper,
    },
  }));



function computeSortedGroups(rows) {
  const idToRow = new Map(rows.map(r => [r.classId, r]));
  const visited = new Set();
  const groups = [];

  // Iterate in alpha order so BFS explores alphabetically within each group
  const alphaSorted = [...rows].sort((a, b) => (a.className || '').localeCompare(b.className || ''));

  for (const row of alphaSorted) {
    if (visited.has(row.classId)) continue;
    const group = [];
    const queue = [row.classId];
    while (queue.length) {
      const id = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      const r = idToRow.get(id);
      if (!r) continue;
      group.push(r);
      (r.relatedClasses || []).forEach(relId => {
        if (!visited.has(relId)) queue.push(relId);
      });
    }
    group.sort((a, b) => (a.className || '').localeCompare(b.className || ''));
    groups.push(group);
  }

  // Multi-member groups first (alpha by first member), then singletons (alpha)
  const multi = groups.filter(g => g.length > 1).sort((a, b) => (a[0].className || '').localeCompare(b[0].className || ''));
  const single = groups.filter(g => g.length === 1).sort((a, b) => (a[0].className || '').localeCompare(b[0].className || ''));
  return [...multi, ...single];
}

function mergeOneToOneRows(rows) {
  const groups = {};
  const groupOrder = [];
  rows.forEach(row => {
    const studentId = row?.student?.tlhStudentId;
    const subject = row?.subject?.toUpperCase();
    const key = studentId && subject ? `${studentId}-${subject}` : `unmerged-${row.classId}`;
    if (!groups[key]) {
      groups[key] = { ...row, lessonRecords: [...(row.lessonRecords || [])] };
      groupOrder.push(key);
    } else {
      const incoming = (row.lessonRecords || []).map((rec, i) => ({ ...rec, _blockStart: i === 0 }));
      groups[key].lessonRecords = [...groups[key].lessonRecords, ...incoming];
    }
  });
  return groupOrder.map(key => {
    const row = groups[key];
    const sorted = [...row.lessonRecords].sort((a, b) => new Date(a.lessonDate) - new Date(b.lessonDate));
    return { ...row, lessonRecords: sorted.map((rec, idx) => ({ ...rec, lessonNumber: idx + 1 })) };
  });
}

export default function TLHLessonPlansPrintedInventory() {
    
    const [lessons, setLessons] = useState([]);
    const [isLoadingLessons, setIsLoadingLessons] = useState(true);
    const [addLessonsModalRow, setAddLessonsModalRow] = useState(null);
    const [lessonPlanChangeModal, setLessonPlanChangeModal] = useState(null); // { rowClassId, recordId, newPlanNumber }
    const history = useHistory();

    useEffect(() => {
        fetchAndMergeLessons();
    }, []);

    function fetchAndMergeLessons() {
        TLHClassesAPICalls.getAllTLHOneToOneLessons()
            .then(result => {
                console.log("One to One Lessons Data");
                console.log(result.data);
                const payload = result.data;
                const lessonArray = Array.isArray(payload) ? payload : (payload ? Object.values(payload) : []);
                setLessons(mergeOneToOneRows(lessonArray));
                setIsLoadingLessons(false);
            })
            .catch(err => {
                console.error("Failed to fetch one-to-one lessons", err);
            });
    }

// The enum order for yearGroup
    const YEAR_GROUP_ORDER = [
        "RECEPTION",
        "YEAR_1",
        "YEAR_2",
        "YEAR_3",
        "YEAR_4",
        "YEAR_5",
        "YEAR_6",
        "YEAR_7_AND_8",
        "YEAR_8_AND_9",
        "KS3_A",
        "KS3_B",
        "KS3_C",
        "GCSE_1",
        "GCSE_2",
        "AS_LEVEL",
    ];

    const sortedGroups = useMemo(() => computeSortedGroups(Array.isArray(lessons) ? lessons : []), [lessons]);

    const maxCols = useMemo(
      () =>
        (Array.isArray(lessons) ? lessons : []).reduce(
          (m, l) => Math.max(m, (l?.lessonRecords?.length ?? 0)),
          0
        ),
      [lessons]
    );

  function updateLessonRecord(recordId, updates) {
    setLessons(prev => prev.map(row => ({
      ...row,
      lessonRecords: (row.lessonRecords || []).map(rec =>
        rec.id === recordId ? { ...rec, ...updates } : rec
      )
    })));
    TLHClassesAPICalls.updateOneToOneLessonRecord(recordId, updates)
      .catch(err => console.error("Failed to update lesson record", err));
  }

  function handleLessonDateChange(rowClassId, recordId, newDate) {
    const currentRow = lessons.find(row => row.classId === rowClassId);
    if (!currentRow) return;

    // Capture the plan number sequence from the current date order — these are the "slots"
    const currentSorted = [...(currentRow.lessonRecords || [])].sort((a, b) => {
      if (!a.lessonDate) return 1;
      if (!b.lessonDate) return -1;
      return new Date(a.lessonDate) - new Date(b.lessonDate);
    });
    const planNumberSlots = currentSorted.map(rec => rec.oneToOneLessonPlanNumber);

    // Apply the new date and re-sort
    const updated = (currentRow.lessonRecords || []).map(rec =>
      rec.id === recordId ? { ...rec, lessonDate: newDate } : rec
    );
    const newSorted = [...updated].sort((a, b) => {
      if (!a.lessonDate) return 1;
      if (!b.lessonDate) return -1;
      return new Date(a.lessonDate) - new Date(b.lessonDate);
    });

    // Reassign lesson numbers and re-flow plan number slots into new positions
    const renumbered = newSorted.map((rec, idx) => ({
      ...rec,
      lessonNumber: idx + 1,
      oneToOneLessonPlanNumber: planNumberSlots[idx],
    }));

    // Update state
    setLessons(prev => prev.map(row =>
      row.classId === rowClassId ? { ...row, lessonRecords: renumbered } : row
    ));

    // Send API updates only for records that actually changed
    renumbered.forEach(rec => {
      const original = currentRow.lessonRecords.find(r => r.id === rec.id);
      const updates = {};
      if (rec.id === recordId) updates.lessonDate = newDate;
      if (original && original.lessonNumber !== rec.lessonNumber) updates.lessonNumber = rec.lessonNumber;
      if (original && original.oneToOneLessonPlanNumber !== rec.oneToOneLessonPlanNumber) updates.oneToOneLessonPlanNumber = rec.oneToOneLessonPlanNumber;
      if (Object.keys(updates).length > 0) {
        TLHClassesAPICalls.updateOneToOneLessonRecord(rec.id, updates)
          .catch(err => console.error("Failed to update lesson record", err));
      }
    });
  }

  function applyLessonPlanChange(updateFuture) {
    const { rowClassId, recordId, newPlanNumber } = lessonPlanChangeModal;
    setLessonPlanChangeModal(null);

    if (!updateFuture) {
      updateLessonRecord(recordId, { oneToOneLessonPlanNumber: newPlanNumber });
      return;
    }

    // Sort this row's records by date to find the changed record's position
    const row = lessons.find(r => r.classId === rowClassId);
    if (!row) return;

    const sorted = [...row.lessonRecords].sort((a, b) => new Date(a.lessonDate) - new Date(b.lessonDate));
    const changedIdx = sorted.findIndex(r => r.id === recordId);
    if (changedIdx === -1) return;

    // Assign sequential plan numbers from changedIdx onwards
    const changed = sorted
      .slice(changedIdx)
      .map((rec, offset) => ({ ...rec, oneToOneLessonPlanNumber: newPlanNumber + offset }))
      .filter((rec, offset) => rec.oneToOneLessonPlanNumber !== sorted[changedIdx + offset]?.oneToOneLessonPlanNumber);

    // Update local state
    const updatedIds = new Map(changed.map(r => [r.id, r.oneToOneLessonPlanNumber]));
    setLessons(prev => prev.map(r =>
      r.classId !== rowClassId ? r : {
        ...r,
        lessonRecords: r.lessonRecords.map(rec =>
          updatedIds.has(rec.id) ? { ...rec, oneToOneLessonPlanNumber: updatedIds.get(rec.id) } : rec
        )
      }
    ));

    // Persist each changed record to the backend
    changed.forEach(rec => {
      TLHClassesAPICalls.updateOneToOneLessonRecord(rec.id, { oneToOneLessonPlanNumber: rec.oneToOneLessonPlanNumber })
        .catch(err => console.error("Failed to update lesson plan number", err));
    });
  }

  function handleLessonsAdded() {
    fetchAndMergeLessons();
  }

  return (
    <div style={{ overflowX: "auto", paddingLeft: '8px' }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          minWidth: Math.max(600, 140 * (maxCols + 1)), // force horizontal room if many lessons
        }}
      >
        <thead>
          <tr>
            {/* top-left corner blank cell for the name column header */}
            <th
              style={{
                textAlign: "left",
                padding: "10px 12px",
                borderBottom: "1px solid #ddd",
                position: "sticky",
                left: 0,
                background: "#fff",
                zIndex: 1,
              }}
            >
              Student / Class
            </th>
            {Array.from({ length: maxCols }).map((_, idx) => (
              <th
                key={`hdr-${idx}`}
                style={{
                  textAlign: "center",
                  padding: "10px 12px",
                  borderBottom: "1px solid #ddd",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                L{idx + 1}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedGroups.flatMap((group, gIdx) => {
            const isMulti = group.length > 1;
            const separatorRow = gIdx === 0 ? null : (
              <tr key={`sep-${gIdx}`}>
                <td
                  colSpan={maxCols + 1}
                  style={{ padding: 0 }}
                >
                  <div style={{
                    height: isMulti ? 6 : 2,
                    background: isMulti ? '#1976d2' : '#e0e0e0',
                    width: '100%',
                  }} />
                </td>
              </tr>
            );
            const rowEls = group.map((row, rIdx) => {
            // determine display name for the left column
            const nameCell =
              row?.studentName || row?.className || row?.name || `Row ${rIdx + 1}`;

            const records = row?.lessonRecords || [];
            const sortedRecords = [...(records || [])].sort((a, b) => {
              const da = new Date(a.lessonDate).getTime();
              const db = new Date(b.lessonDate).getTime();
              return da - db;
            });

            return (
              <tr key={row?.id ?? row?.classId ?? `row-${rIdx}`}>
                {/* Left fixed name column */}
                <td
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid #eee",
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    background: "#fff",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={nameCell}
                >
                  <button
                    onClick={() => row?.student?.tlhStudentId && history.push(CONSTANTS.ViewTLHStudentProfile + "/" + row.student.tlhStudentId)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: row?.student?.tlhStudentId ? 'pointer' : 'default',
                      textDecoration: row?.student?.tlhStudentId ? 'underline' : 'none',
                      fontWeight: 600,
                      fontSize: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    {nameCell}
                  </button>
                </td>

                {/* Lesson cells */}
                {Array.from({ length: maxCols + 1 }).map((_, cIdx) => {
                  const rec = cIdx < sortedRecords.length ? sortedRecords[cIdx] : null;

                  return (
                    <td
                      key={`cell-${rIdx}-${cIdx}`}
                      style={{
                        padding: 8,
                        paddingTop: 24,
                        borderBottom: "1px solid #f0f0f0",
                        verticalAlign: "top",
                        height: 1, // allows child button to use height: '100%'
                      }}
                    >
                      {rec ? (
                        <Paper sx={rec._blockStart ? {
                          borderLeft: '4px solid #1976d2',
                          position: 'relative',
                          '&::before': {
                            content: '"new block"',
                            position: 'absolute',
                            top: -20,
                            left: 0,
                            fontSize: '0.65rem',
                            fontWeight: 800,
                            color: '#1976d2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px',
                          }
                        } : {}}>
                          <OneToOneIndividualLessonCard
                            lessonNumber={rec.lessonNumber}
                            level={rec.oneToOneLessonPlanYearGroup || row.yearGroup}
                            lessonPlanNumber={rec.oneToOneLessonPlanNumber}
                            date={rec.lessonDate}
                            time={rec.startTime}
                            status={mapAttendanceStatus(rec.attendanceStatus)}
                            classLessonRecordId={rec.id}
                            onStatusChange={(cardStatus) => updateLessonRecord(rec.id, { attendanceStatus: mapCardStatusToBackend(cardStatus) })}
                            onDateChange={(v) => handleLessonDateChange(row.classId, rec.id, v)}
                            onTimeChange={(v) => updateLessonRecord(rec.id, { startTime: v })}
                            onLevelChange={(v) => updateLessonRecord(rec.id, { oneToOneLessonPlanYearGroup: v })}
                            onLessonPlanChange={(v) => setLessonPlanChangeModal({ rowClassId: row.classId, recordId: rec.id, newPlanNumber: v })}
                          />
                        </Paper>
                      ) : cIdx === sortedRecords.length ? (
                        <button
                          onClick={() => setAddLessonsModalRow(row)}
                          style={{
                            width: 152,
                            height: '100%',
                            borderRadius: 8,
                            border: '2px dashed #bdbdbd',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 28,
                            color: '#9e9e9e',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            padding: '8px',
                          }}
                          title="Add more lessons"
                        >
                          +
                        </button>
                      ) : (
                        <div style={{ height: 64 }} />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
            });
            return separatorRow ? [separatorRow, ...rowEls] : rowEls;
          })}
        </tbody>
      </table>

      <AddLessonsModal
        open={addLessonsModalRow !== null}
        row={addLessonsModalRow}
        onClose={() => setAddLessonsModalRow(null)}
        onLessonsAdded={() => {
          handleLessonsAdded();
          setAddLessonsModalRow(null);
        }}
      />

      {/* Lesson plan update modal */}
      <Modal open={lessonPlanChangeModal !== null} onClose={() => applyLessonPlanChange(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper', borderRadius: 2,
          boxShadow: 24, p: 4, width: 360,
        }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Update Lesson Plan</Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Would you like to update all following lesson plan numbers sequentially from this lesson onwards, or just update this one?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="contained" fullWidth onClick={() => applyLessonPlanChange(true)}>
              Update this and all following lessons
            </Button>
            <Button variant="outlined" fullWidth onClick={() => applyLessonPlanChange(false)}>
              Just this lesson
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

function AddLessonsModal({ open, row, onClose, onLessonsAdded }) {
  const studentName = row?.studentName || row?.className || '';
  const [subject, setSubject] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [yearGroup, setYearGroup] = useState('');
  const [scheduledLessons, setScheduledLessons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && row) {
      setSubject(row.subject?.toLowerCase() || '');
      setRoomNumber(row.roomNumber?.toLowerCase() || '');
      setYearGroup(row.yearGroup?.toLowerCase() || '');
      setScheduledLessons([]);
    }
  }, [open, row]);

  function addLesson() {
    let prev;
    if (scheduledLessons.length > 0) {
      prev = scheduledLessons[scheduledLessons.length - 1];
    } else {
      // First lesson being added — seed from the last existing lesson record in this row
      const existing = [...(row?.lessonRecords || [])].sort((a, b) => new Date(a.lessonDate) - new Date(b.lessonDate));
      const last = existing.length > 0 ? existing[existing.length - 1] : null;
      if (last) {
        prev = {
          date: last.lessonDate,
          time: last.startTime ? last.startTime.substring(0, 5) : '',
          duration: last.duration,
        };
      }
    }
    let nextDate = '';
    if (prev?.date) {
      const d = new Date(prev.date);
      d.setDate(d.getDate() + 7);
      nextDate = d.toISOString().split('T')[0];
    }
    setScheduledLessons([...scheduledLessons, { date: nextDate, time: prev?.time || '', duration: prev?.duration || '' }]);
  }

  function removeLesson(idx) {
    setScheduledLessons(scheduledLessons.filter((_, i) => i !== idx));
  }

  function updateLesson(idx, field, value) {
    const updated = [...scheduledLessons];
    updated[idx] = { ...updated[idx], [field]: value };
    setScheduledLessons(updated);
  }

  const totalMinutes = scheduledLessons.reduce((sum, l) => sum + (parseInt(l.duration, 10) || 0), 0);

  function handleSubmit() {
    if (scheduledLessons.length === 0) return;
    if (!roomNumber) { alert('Please select a room.'); return; }
    setSubmitting(true);
    const studentName = [row?.student?.firstName, row?.student?.surname].filter(Boolean).join(' ');
const sortedDates = [...scheduledLessons].map(l => l.date).filter(Boolean).sort();
    const classData = {
      classType: 'ONE_TO_ONE',
      className: `${studentName} - ${subject.toUpperCase()}`,
      subject: subject.toUpperCase(),
      yearGroup: yearGroup.toUpperCase(),
      classStartDate: sortedDates[0] || '',
      classEndDate: sortedDates[sortedDates.length - 1] || '',
      startTime: scheduledLessons[0]?.time || '',
      classDurationMins: parseInt(scheduledLessons[0]?.duration, 10) || 60,
      relatedClasses: [],
      duplicateClasses: [],
      lessons: scheduledLessons,
      tlhStudentName: studentName,
      oneToOneCurrentTLHStudentId: row?.student?.tlhStudentId,
      studentName: '',
      roomNumber: roomNumber.toUpperCase(),
    };
    TLHClassesAPICalls.createClassFromTLHClassesDashboard(classData)
      .then(() => onLessonsAdded())
      .catch(err => console.error('Failed to create lessons', err))
      .finally(() => setSubmitting(false));
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper', borderRadius: 2,
        boxShadow: 24, p: 4, width: 580, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Add Lessons</Typography>

        <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: 'text.secondary' }}>
          Student: {studentName}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Subject</InputLabel>
            <Select value={subject} label="Subject" onChange={(e) => setSubject(e.target.value)}>
              {CONSTANTS.TLHSubjects.map((s) => (
                <MenuItem key={s} value={s.toLowerCase()}>
                  {s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Room</InputLabel>
            <Select value={roomNumber} label="Room" onChange={(e) => setRoomNumber(e.target.value)}>
              {CONSTANTS.TLHRooms.map((r) => (
                <MenuItem key={r} value={r.toLowerCase()}>
                  {r.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Level</InputLabel>
            <Select value={yearGroup} label="Level" onChange={(e) => setYearGroup(e.target.value)}>
              {CONSTANTS.TLHYearGroups.map((y) => (
                <MenuItem key={y} value={y.toLowerCase()}>
                  {y.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Scheduled Lessons</Typography>

        {scheduledLessons.map((lesson, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
            <TextField
              label="Date"
              type="date"
              value={lesson.date}
              onChange={(e) => updateLesson(idx, 'date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1 }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label="Time"
                value={(() => {
                  if (!lesson.time) return null;
                  const [h, m] = lesson.time.split(':').map(Number);
                  if (isNaN(h) || isNaN(m)) return null;
                  const d = new Date(); d.setHours(h, m, 0, 0); return d;
                })()}
                onChange={(val) => {
                  const t = val
                    ? String(val.getHours()).padStart(2, '0') + ':' + String(val.getMinutes()).padStart(2, '0')
                    : '';
                  updateLesson(idx, 'time', t);
                }}
                renderInput={(params) => <TextField {...params} size="small" sx={{ flex: 1 }} />}
              />
            </LocalizationProvider>
            <TextField
              label="Duration (mins)"
              type="number"
              value={lesson.duration}
              onChange={(e) => updateLesson(idx, 'duration', e.target.value)}
              inputProps={{ min: 1 }}
              size="small"
              sx={{ flex: 1 }}
            />
            <button type="button" onClick={() => removeLesson(idx)} style={{ flexShrink: 0 }}>❌</button>
          </Box>
        ))}

        <button type="button" onClick={addLesson} style={{ marginBottom: 12 }}>➕ Add Lesson</button>

        {scheduledLessons.length > 0 && (
          <Box sx={{
            mt: 2, mb: 2, px: 2, py: 1.5,
            background: '#e8f4fd',
            borderRadius: 2,
            border: '1px solid #90caf9',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Time Booked
            </Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#0d47a1' }}>
              {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={scheduledLessons.length === 0 || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Saving...' : 'Add Lessons'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
